import React from "react";
import { Box, Typography } from "@mui/material";
import { colors } from "../../theme/colors";
import {
    StatBarChart,
    StatMetricSparkline,
    StatRingChart,
} from "./profileStatsBarCharts";
import { ProfileStatsBarSkeleton } from "./profileSkeletons";

const STAT_SEPARATOR_SX = {
    width: "1px",
    alignSelf: "stretch",
    flexShrink: 0,
    background: `
        linear-gradient(
            180deg,
            transparent,
            rgba(255,255,255,0.12),
            transparent
        )
    `,
};

const STAT_VISUAL_SX = {
    flexShrink: 0,
    "@media (min-width: 1050px) and (max-width: 1279.95px)": {
        display: "none",
    },
};

const MOBILE_STAT_CARD_SX = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: "0.35rem",
    px: "0.85rem",
    py: "0.75rem",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.05)",
    background: `
        linear-gradient(
            180deg,
            rgba(16,16,18,0.92) 0%,
            rgba(10,10,12,0.96) 100%
        )
    `,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
    boxSizing: "border-box",
};

function hasMeaningfulStatValue(value) {
    if (value == null) return false;

    const raw = String(value).trim();
    if (!raw || raw === "—" || raw === "–" || raw === "-") return false;

    const numeric = Number(raw.replace(/%/g, "").replace(/,/g, ""));
    if (Number.isFinite(numeric)) {
        return numeric !== 0;
    }

    return true;
}

function hasAnyStatData(stats) {
    return Array.isArray(stats) && stats.some((stat) => hasMeaningfulStatValue(stat?.value));
}

function StatVisual({ visualType, visualData }) {
    if (visualType === "bar") {
        return <StatBarChart data={visualData} />;
    }
    if (visualType === "ring") {
        return <StatRingChart value={visualData} />;
    }
    if (visualType === "sparkline") {
        return <StatMetricSparkline values={visualData} />;
    }
    return null;
}

function MobileStatCard({ label, value, sublabel, valueColor }) {
    return (
        <Box sx={MOBILE_STAT_CARD_SX}>
            <Typography
                sx={{
                    color: colors.text.secondary,
                    fontSize: "0.58rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    lineHeight: 1,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    color: valueColor || colors.text.primary,
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {value}
            </Typography>
            {sublabel ? (
                <Typography
                    sx={{
                        color: colors.text.muted,
                        fontSize: "0.55rem",
                        fontWeight: 500,
                        letterSpacing: "0.1em",
                        lineHeight: 1,
                        textTransform: "uppercase",
                    }}
                >
                    {sublabel}
                </Typography>
            ) : null}
        </Box>
    );
}

function DesktopStatCell({
    label,
    value,
    sublabel,
    valueColor,
    visualType,
    visualData,
}) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 1,
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.65rem",
                py: "0.85rem",
                px: "1rem",
                background: "transparent",
                minHeight: "4.25rem",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    minWidth: 0,
                    flex: 1,
                }}
            >
                <Typography
                    sx={{
                        color: colors.text.secondary,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        lineHeight: 1,
                        mb: "0.45rem",
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        color: valueColor || colors.text.primary,
                        fontSize: "1.3rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {value}
                </Typography>
                {sublabel ? (
                    <Typography
                        sx={{
                            color: colors.text.muted,
                            fontSize: "0.58rem",
                            fontWeight: 500,
                            letterSpacing: "0.10em",
                            lineHeight: 1,
                            mt: "0.2rem",
                            textTransform: "uppercase",
                        }}
                    >
                        {sublabel}
                    </Typography>
                ) : null}
            </Box>
            <Box sx={STAT_VISUAL_SX}>
                <StatVisual visualType={visualType} visualData={visualData} />
            </Box>
        </Box>
    );
}

export default function ProfileStatsBar({ stats = [], isLoading = false }) {
    if (isLoading) {
        return <ProfileStatsBarSkeleton />;
    }

    if (!hasAnyStatData(stats)) {
        return null;
    }

    return (
        <>
            {/* Mobile: separate cards in a 2-column grid */}
            <Box
                sx={{
                    display: { xs: "grid", md: "none" },
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "0.65rem",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    alignSelf: "stretch",
                }}
            >
                {stats.map((stat) => (
                    <MobileStatCard
                        key={stat.label}
                        label={stat.label}
                        value={stat.value}
                        sublabel={stat.sublabel}
                        valueColor={stat.valueColor}
                    />
                ))}
            </Box>

            {/* Desktop: single shared container */}
            <Box
                sx={{
                    position: "relative",
                    display: { xs: "none", md: "flex" },
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    alignSelf: "stretch",
                    alignItems: "stretch",
                    overflow: "hidden",
                    borderRadius: "12px",
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(12,12,14,0.96) 0%,
                            rgba(8,8,10,0.985) 100%
                        )
                    `,
                    border: "1px solid rgba(255,255,255,0.05)",
                    backdropFilter: "blur(18px)",
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.05),
                        inset 0 -40px 80px rgba(246,185,76,0.03),
                        0 24px 60px rgba(0,0,0,0.42)
                    `,
                }}
            >
                {stats.map((stat, index) => (
                    <React.Fragment key={stat.label}>
                        <DesktopStatCell {...stat} />
                        {index < stats.length - 1 ? (
                            <Box sx={STAT_SEPARATOR_SX} />
                        ) : null}
                    </React.Fragment>
                ))}
            </Box>
        </>
    );
}
